import { FC, useEffect, useState } from 'react';

import { daysFromNow, parse, isValid, DATE_FORMAT } from '@eneco/common/date';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import useDC from '@eneco/dc/useDC';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useFormatter } from '@eneco/i18n';
import { usePlaceholderContent } from '@sitecore/common';
import { OxxioNewDeliveryDateStepRendering } from '@sitecore/types/OxxioNewDeliveryDateStep';

import { FlowConsumerContextEnergy } from '../../../context';
import { DeliveryDateFormFields, DeliveryDateMovingNotification, DeliveryDatePicker } from '../../../shared/components';
import { StepShell } from '../../../shared/components/StepShell/StepShell';
import {
  getMinContractStartDateInDays,
  getMaxContractStartDateInDays,
  getMaxUrgentContractStartDateInDays,
  CONTRACT_CONSIDERATION_PERIOD_IN_DAYS,
  buildDeliveryDateFormSchema,
} from '../../../shared/helpers';
import { useFlowStep } from '../../../shared/hooks';

export const DeliveryDateStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const {
    context: { isMoving, consentConsiderationPeriod, deliveryDate },
  } = useFlowSelector(state => state);

  const { label } = useDC();
  const { format, date } = useFormatter();

  const minimumDays = getMinContractStartDateInDays(!!isMoving, false, label);
  const maximumDays = getMaxContractStartDateInDays(label);
  const maximumUrgentDays = getMaxUrgentContractStartDateInDays(label);

  const minimumDate = daysFromNow(isMoving ? 0 : minimumDays);
  const maximumDate = daysFromNow(maximumDays);
  const defaultDate = date.short(daysFromNow(getMinContractStartDateInDays(false, false, label)));

  const [shouldShowExtraField, setShouldShowExtraField] = useState(!!(consentConsiderationPeriod && isMoving));
  const [shouldShowBlockingNotification, setShouldShowBlockingNotification] = useState(false);
  const [shouldShowNonBlockingNotification, setShouldShowNonBlockingNotification] = useState(false);

  const { OxxioNewDeliveryDateStep: { fields: textualData } = {} } = usePlaceholderContent<{
    OxxioNewDeliveryDateStep: OxxioNewDeliveryDateStepRendering;
  }>();

  const {
    form: {
      handleSubmit,
      setValue,
      register,
      watch,
      control,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<DeliveryDateFormFields>({
    buildSchema: () =>
      buildDeliveryDateFormSchema({
        isMoving,
        minimumDate,
        maximumDate,
        minimumDays,
        shouldShowExtraField,
        format,
        dateShort: date.short,
        textualData,
      }),
    defaultValues: () => ({
      deliveryDate: deliveryDate ?? defaultDate,
      consentConsiderationPeriod,
    }),
    onSubmitSuccess: formField => ({ ...formField }),
  });

  const mutatedDeliveryDate = watch('deliveryDate') || '';

  useEffect(() => {
    const currentDate = parse(mutatedDeliveryDate, DATE_FORMAT);

    if (isMoving) {
      setShouldShowExtraField(
        isValid(currentDate) &&
          currentDate > daysFromNow(maximumUrgentDays) &&
          currentDate <= daysFromNow(CONTRACT_CONSIDERATION_PERIOD_IN_DAYS),
      );

      setShouldShowBlockingNotification(isValid(currentDate) && currentDate <= daysFromNow(maximumUrgentDays));
    }

    setShouldShowNonBlockingNotification(
      isValid(currentDate) &&
        currentDate > daysFromNow(maximumUrgentDays) &&
        currentDate > daysFromNow(CONTRACT_CONSIDERATION_PERIOD_IN_DAYS),
    );
  }, [isMoving, maximumUrgentDays, mutatedDeliveryDate]);

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={textualData?.content?.nextStepText?.value}
      showNextButton={!shouldShowBlockingNotification}>
      <DeliveryDatePicker
        control={control}
        register={register}
        setValue={setValue}
        errors={errors}
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        shouldShowExtraField={shouldShowExtraField}
        mutatedDeliveryDate={mutatedDeliveryDate}
        textualData={{
          label: textualData?.deliveryDate?.deliveryDateFormField?.value?.label ?? '',
          placeholder: textualData?.deliveryDate?.deliveryDateFormField?.value?.placeholder,
          hint: textualData?.deliveryDate?.deliveryDateFormField?.value?.hint,
          extraFieldLabel: textualData?.deliveryDate?.isUrgentFormField?.value?.label ?? '',
        }}
      />

      {shouldShowNonBlockingNotification && (
        <DeliveryDateMovingNotification isMoving={isMoving} textualData={textualData?.notifications} />
      )}

      {shouldShowBlockingNotification && (
        <TrackedNotificationBox
          label={textualData?.notifications?.urgentDateNotification?.value?.title ?? ''}
          title={textualData?.notifications?.urgentDateNotification?.value?.title}
          isAlert
          text={<RichText html={textualData?.notifications?.urgentDateNotification?.value?.content} />}
          variant={textualData?.notifications?.urgentDateNotification?.value?.variant}
        />
      )}
    </StepShell>
  );
};
